import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import HeaderLogo from '@comp/icons/header-logo'

const EASE = [0.22, 1, 0.36, 1] as const

type Variant = {
  label: string
  caption: string
  background: 'light' | 'dark' | 'brand'
  logo: 'wordmark' | 'square'
}

const variants: Variant[] = [
  {
    label: 'Primary · light background',
    caption: 'Wordmark in navy on white. Default for printed materials and reports.',
    background: 'light',
    logo: 'wordmark',
  },
  {
    label: 'Primary · dark background',
    caption: 'Wordmark in white on Eerie Black. Default for web headers and dark layouts.',
    background: 'dark',
    logo: 'wordmark',
  },
  {
    label: 'Primary · brand background',
    caption: 'Wordmark in white on Madison Blue. Use for hero placements and event signage.',
    background: 'brand',
    logo: 'wordmark',
  },
  {
    label: 'Square mark · for avatars and favicons',
    caption: 'The Monofor mark on its own. Use only where the wordmark will not legibly fit.',
    background: 'dark',
    logo: 'square',
  },
]

const backgroundClass: Record<Variant['background'], string> = {
  light: 'bg-white text-brand-900 border border-line-subtle',
  dark: 'bg-[#191919] text-white',
  brand: 'bg-brand-900 text-white',
}

export default function LogoGallery() {
  return (
    <Section tone="subtle" density="default" id="logos">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow>Logos</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            Four approved
            <br />
            ways to show the mark.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            Use the wordmark wherever it will fit legibly. Reach for the square
            mark only for avatars, favicons, or tight square layouts.
          </Text>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {variants.map((v, i) => (
            <motion.div
              key={v.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: (i % 2) * 0.06 }}
              className="overflow-hidden rounded-3xl border border-line-subtle bg-surface shadow-card"
            >
              <div
                className={`flex aspect-[16/9] items-center justify-center ${backgroundClass[v.background]}`}
              >
                {v.logo === 'wordmark' ? (
                  <HeaderLogo />
                ) : (
                  <SquareMark />
                )}
              </div>
              <div className="border-t border-line-subtle p-6">
                <div className="text-body-md font-medium text-ink-primary">
                  {v.label}
                </div>
                <div className="mt-1 text-body-sm text-ink-tertiary">
                  {v.caption}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function SquareMark() {
  // Same petals as the favicon, sized for the gallery card.
  const paths = [
    'M171.859 135.905C176.326 134.39 180.873 133.033 185.5 131.757C207.995 125.615 231.607 122.264 256.097 122.264C280.586 122.264 304.198 125.615 326.694 131.757C331.32 133.033 335.787 134.39 340.255 135.905C347.913 138.458 355.411 141.33 362.75 144.6C374.955 149.945 386.681 156.167 397.849 163.187C397.53 154.651 396.812 146.116 395.615 137.66C394.898 132.475 394.02 127.21 393.063 122.025C380.698 115.643 367.776 110.139 354.454 105.513C346.397 102.721 338.181 100.248 329.805 98.0939C324.859 96.8175 319.913 95.7007 314.888 94.7435C295.902 90.9943 276.199 89 256.097 89C235.915 89 216.291 90.9943 197.306 94.7435C192.28 95.7007 187.334 96.8973 182.388 98.0939C174.013 100.248 165.796 102.721 157.739 105.513C144.418 110.139 131.575 115.643 119.13 122.025C118.093 127.21 117.216 132.395 116.498 137.66C115.381 146.116 114.663 154.651 114.264 163.187C125.432 156.167 137.159 149.945 149.363 144.6C156.782 141.409 164.281 138.538 171.859 135.905Z',
    'M418.829 177.944C412.048 172.68 405.028 167.814 397.769 163.187C398.248 176.349 397.769 189.511 396.254 202.753C395.376 210.65 394.1 218.548 392.425 226.445C391.467 231.072 390.351 235.619 389.154 240.245C383.65 260.826 375.593 281.008 364.824 300.472C352.22 323.207 336.665 343.389 319.036 360.779C315.605 364.129 312.175 367.32 308.586 370.511C302.523 375.856 296.301 380.881 289.84 385.667C279.07 393.565 267.823 400.505 256.097 406.647C263.755 410.636 271.572 414.225 279.549 417.496C284.495 419.49 289.441 421.325 294.466 423C306.193 415.502 317.52 407.046 328.209 397.872C334.671 392.288 340.893 386.385 346.955 380.243C350.465 376.573 353.975 372.824 357.326 368.995C371.206 353.201 383.57 335.731 394.18 316.586C402.795 301.11 409.815 285.156 415.399 269.043C417.074 264.256 418.589 259.39 420.025 254.524C422.418 246.308 424.413 237.932 426.008 229.636C428.72 215.835 430.395 201.955 431.113 188.075C427.125 184.565 423.057 181.215 418.829 177.944Z',
    'M203.528 370.431C200.018 367.32 196.508 364.049 193.078 360.699C177.124 344.984 162.924 327.036 150.959 306.854C138.275 285.475 129.022 262.98 122.88 240.086C121.683 235.539 120.566 230.912 119.609 226.285C117.934 218.388 116.657 210.491 115.78 202.594C114.264 189.431 113.786 176.189 114.264 163.027C107.005 167.654 99.9854 172.52 93.2049 177.785C89.0568 181.055 84.9885 184.406 81 187.836C81.7179 201.716 83.3931 215.596 86.1053 229.397C87.7007 237.772 89.695 246.069 92.0881 254.285C93.524 259.151 95.0396 264.017 96.7148 268.803C103.176 287.549 111.632 305.976 122.162 323.685C131.814 339.959 142.822 355.035 154.868 368.756C158.218 372.585 161.728 376.334 165.238 380.004C171.221 386.146 177.522 392.049 183.984 397.633C194.673 406.886 206.001 415.262 217.727 422.761C222.752 421.086 227.698 419.251 232.644 417.257C240.621 414.066 248.439 410.396 256.097 406.408C244.37 400.265 233.123 393.246 222.274 385.428C215.812 380.881 209.59 375.776 203.528 370.431Z',
    'M335.548 284.279C347.275 263.06 355.331 240.883 359.958 218.308C360.836 213.921 361.633 209.533 362.271 205.066C363.308 197.488 364.026 189.91 364.345 182.332C357.645 178.822 350.705 175.551 343.605 172.679C339.457 171.004 335.229 169.409 331.001 167.973C307.549 159.996 282.341 155.688 256.176 155.688C230.012 155.688 204.804 159.996 181.352 167.973C177.124 169.409 172.896 171.004 168.748 172.679C161.648 175.551 154.708 178.742 148.007 182.332C148.326 189.91 149.044 197.488 150.081 205.066C150.72 209.454 151.438 213.921 152.395 218.308C157.341 242.878 166.514 267.128 179.995 289.942C192.918 311.72 208.872 330.625 226.98 346.42C230.41 349.372 233.841 352.243 237.43 354.955C243.493 359.662 249.795 363.97 256.176 368.038C262.638 363.97 268.94 359.662 275.002 354.955C278.512 352.243 282.022 349.372 285.452 346.42C304.996 329.269 322.147 308.449 335.548 284.279Z',
  ]
  return (
    <svg width="120" height="120" viewBox="0 0 512 512" fill="none" aria-hidden>
      {paths.map((d, i) => (
        <path key={i} d={d} fill="currentColor" />
      ))}
    </svg>
  )
}
